import { useCallback, useEffect, useRef, useState } from "react"
import {
  useGetPublicAiHistoryQuery,
  useSendPublicAiMessageMutation,
} from "../../../../app/services/book/aiChatBot"

const debugEnabled = () => {
  try {
    return (
      /[?&]aichat_debug=1/.test(window.location.search) ||
      localStorage.getItem("ai_chat_debug") === "1"
    )
  } catch {
    return false
  }
}
const debug = (...args) => {
  if (debugEnabled()) console.log("[ai-chat]", ...args)
}
const warn = (...args) => console.warn("[ai-chat]", ...args)

const STORAGE_PREFIX = "ai_public_token:"
const STAR = "\u2B50"

const keyFromEndpoint = (endpoint) => {
  const text = String(endpoint || "")
  return text.match(/public_ai\/([^/?#]+)/)?.[1] || text.replace(/^\/+|\/+$/g, "")
}

const readToken = (key) => {
  try {
    return sessionStorage.getItem(STORAGE_PREFIX + key)
  } catch {
    return null
  }
}
const writeToken = (key, token) => {
  try {
    sessionStorage.setItem(STORAGE_PREFIX + key, token)
  } catch {}
}
const dropToken = (key) => {
  try {
    sessionStorage.removeItem(STORAGE_PREFIX + key)
  } catch {}
}

export const clearAiChatSession = (endpoint) => dropToken(keyFromEndpoint(endpoint))

const formatTime = (value) =>
  (value ? new Date(value) : new Date()).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  })

const starsOf = (name) => {
  const text = String(name || "")
  const emoji = text.split(STAR).length - 1
  if (emoji > 0) return emoji
  const digit = parseInt(text.match(/\d/)?.[0], 10)
  return Number.isNaN(digit) ? 0 : digit
}

const formatPrice = (value) => {
  const number = Number(value)
  return Number.isFinite(number) ? Math.round(number).toLocaleString() : value
}

// Server offer (already sanitized by the controller) -> card shape expected by HotelCards.
const offerToHotel = (offer, index) => ({
  id: `${offer.hotel_name}-${offer.from_date}-${index}`,
  name: offer.hotel_name,
  stars: starsOf(offer.star_name),
  location: [offer.resort_name, offer.region_name].filter(Boolean).join(", "),
  board: offer.board,
  price: formatPrice(offer.client_price ?? offer.price),
  currency: offer.client_currency ?? offer.currency,
  nights: offer.nights,
  url: offer.book_url,
  image: offer.photo,
})

const toWidgetMessage = (message, id) => ({
  id,
  role: message.role === "user" ? "user" : "bot",
  text: message.content,
  time: formatTime(message.created_at),
  hotels: (message.offers || []).map(offerToHotel),
})

const useAiChat = ({ endpoint, greeting }) => {
  const key = keyFromEndpoint(endpoint)
  const greetingMessage = useCallback(
    () => ({ id: "greeting", role: "bot", text: greeting, time: formatTime(), quickActions: true }),
    [greeting]
  )

  const [messages, setMessages] = useState(() => [greetingMessage()])
  const [isLoading, setIsLoading] = useState(false)
  const [initialToken] = useState(() => readToken(key))
  const tokenRef = useRef(initialToken)
  const requestRef = useRef(null)
  const stoppedRef = useRef(false)
  const loadingRef = useRef(false)
  const restoredRef = useRef(false)

  const [sendMessage] = useSendPublicAiMessageMutation()
  // History is requested only for a token that already existed when the widget opened.
  const history = useGetPublicAiHistoryQuery(
    { key, token: initialToken },
    { skip: !initialToken, refetchOnMountOrArgChange: true }
  )

  useEffect(() => {
    debug("init", { hasToken: !!initialToken })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const items = history.data?.messages
    if (!items || restoredRef.current) return
    restoredRef.current = true
    debug("history restored:", items.length, "messages")
    if (items.length) {
      setMessages((prev) =>
        prev.length > 1
          ? prev
          : [
              greetingMessage(),
              ...items.map((message, index) => toWidgetMessage(message, `h-${index}`)),
            ]
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history.data])

  useEffect(() => {
    if (history.error)
      warn("history failed:", history.error.status, history.error.data?.error || "")
  }, [history.error])

  const send = useCallback(
    async (raw) => {
      const text = String(raw || "").trim()
      if (!text || loadingRef.current) return

      debug("send", { chars: text.length, hasToken: !!tokenRef.current })
      loadingRef.current = true
      stoppedRef.current = false
      setIsLoading(true)
      setMessages((prev) => [
        ...prev,
        { id: `local-${Date.now()}`, role: "user", text, time: formatTime() },
      ])

      const startedAt = performance.now()
      const request = sendMessage({ key, text, token: tokenRef.current })
      requestRef.current = request

      try {
        const data = await request.unwrap()
        debug("reply", {
          ms: Math.round(performance.now() - startedAt),
          offers: data.offers?.length || 0,
          newToken: !!data.token && data.token !== tokenRef.current,
        })

        if (data.token && data.token !== tokenRef.current) {
          tokenRef.current = data.token
          writeToken(key, data.token)
        }

        setMessages((prev) => [
          ...prev,
          toWidgetMessage(
            { role: "bot", content: data.reply, offers: data.offers, created_at: new Date() },
            `bot-${Date.now()}`
          ),
        ])
      } catch (error) {
        if (stoppedRef.current) {
          debug("stopped by the user")
          return
        }
        warn(
          "send failed:",
          error?.status ?? error?.name ?? "",
          error?.data?.error ?? error?.message ?? ""
        )
        setMessages((prev) => [
          ...prev,
          { id: `error-${Date.now()}`, role: "bot", error: true, text: "", time: formatTime() },
        ])
      } finally {
        loadingRef.current = false
        requestRef.current = null
        setIsLoading(false)
      }
    },
    [key, sendMessage]
  )

  const stop = useCallback(() => {
    stoppedRef.current = true
    requestRef.current?.abort()
  }, [])

  useEffect(
    () => () => {
      stoppedRef.current = true
      requestRef.current?.abort()
    },
    []
  )

  return { messages, isLoading, send, stop }
}

export default useAiChat
